import { FC, ReactElement, ReactNode, useMemo, useState } from 'react';

import eyeIcon from '@/assets/eyeIcon.png';
import trashIcon from '@/assets/trashIcon.png';
import Tooltip from '@/components/common/Tooltip.tsx';
import Button from '@/components/controls/Button';

type ColumnWidth = 'auto' | number | string;

export type Column<T> = {
  key: keyof T;
  label: string;
  sortable?: boolean;
  width?: ColumnWidth;
  formattingFunc?: (value: T[keyof T]) => string;
};

type WithId = { id: string };

type CustomEntityTableProps<T extends WithId> = {
  columns: Column<T>[];
  data: T[];
  totalData: number;
  iconName: string;
  isLoading: boolean;
  displayActions?: boolean;
  onView?: (item: T) => void;
  onDelete?: (item: T) => void;
  title: string;
  onAdd?: () => void;
  addButtonText?: string;
  deleteTooltipText?: string;
  subHeader?: ReactNode;
};

type HeaderSectionProps = {
  title: string;
  onAdd?: () => void;
  addButtonText: string;
  totalData: number;
  iconName: string;
};

const HeaderSection: FC<HeaderSectionProps> = ({
  title,
  onAdd,
  addButtonText,
  totalData,
  iconName,
}) => (
  <div className="flex flex-col sm:flex-row sm:justify-between items-start sm:items-center gap-4 mb-4 ml-3 py-2">
    <div className="flex items-center gap-4 text-primary-300">
      <img
        src={iconName}
        alt=""
        className="w-8 h-8 object-contain brightness-0"
        aria-hidden="true"
      />
      <h2 className="text-2xl font-semibold">{`${title} (${totalData})`}</h2>
    </div>

    {onAdd ? (
      <Button
        text={addButtonText}
        buttonStyle="bg-honoursOrange hover:bg-honoursOrange/90 text-white text-sm px-8 py-2 rounded-lg w-full sm:w-auto"
        buttonType={{ type: 'button' }}
        hasImage={false}
        onClick={onAdd}
      />
    ) : null}
  </div>
);

type TableDataRowProps<T> = {
  item: T;
  columns: Column<T>[];
  onView: (item: T) => void;
  onDelete: (item: T) => void;
  displayActions: boolean;
  deleteTooltipText?: string;
};

type TableDataRowActionsProps<T> = {
  item: T;
  onView: (item: T) => void;
  onDelete: (item: T) => void;
  deleteTooltipText?: string;
};

const TableDataRowActions = <T extends WithId>({
  item,
  onView,
  onDelete,
  deleteTooltipText,
}: TableDataRowActionsProps<T>): ReactElement => {
  return (
    <td className="px-4 py-3">
      <div className="flex gap-4 items-center justify-center">
        <Tooltip label="View">
          <img
            src={eyeIcon}
            alt="View details"
            className="h-3.5 w-auto cursor-pointer"
            onClick={() => onView(item)}
          />
        </Tooltip>

        {onDelete ? (
          <Tooltip label={deleteTooltipText || 'Delete'}>
            <img
              src={trashIcon}
              alt="Delete"
              className="h-5 w-auto cursor-pointer"
              onClick={() => onDelete(item)}
            />
          </Tooltip>
        ) : null}
      </div>
    </td>
  );
};

function getDisplayValue<T>(item: T, column: Column<T>): string {
  const rawValue = item[column.key];
  return column.formattingFunc
    ? column.formattingFunc(rawValue)
    : String(rawValue);
}

const TableDataRow = <T extends WithId>({
  item,
  columns,
  onView,
  onDelete,
  displayActions,
  deleteTooltipText,
}: TableDataRowProps<T>): ReactElement => {
  return (
    <tr key={item.id} className="hover:bg-gray-50 transition-colors">
      {columns.map((col) => (
        <td key={String(col.key)} className="px-4 py-4">
          {getDisplayValue(item, col)}
        </td>
      ))}

      {displayActions ? (
        <TableDataRowActions
          item={item}
          onView={onView}
          onDelete={onDelete}
          deleteTooltipText={deleteTooltipText}
        />
      ) : null}
    </tr>
  );
};

const LoadingSpinner: FC = () => (
  <div className="flex justify-center items-center py-12 w-full">
    <div className="animate-spin rounded-full h-10 w-10 border-4 border-gray-200 border-t-gray-500" />
  </div>
);

type TableSectionProps<T> = {
  columns: Column<T>[];
  data: T[];
  isLoading: boolean;
  sortConfig: { key: keyof T; direction: 'asc' | 'desc' } | null;
  onSort: (key: keyof T) => void;
  onView: (item: T) => void;
  onDelete: (item: T) => void;
  displayActions: boolean;
  deleteTooltipText?: string;
};

const TableSection = <T extends WithId>({
  columns,
  data,
  isLoading,
  sortConfig,
  onSort,
  onView,
  onDelete,
  displayActions,
  deleteTooltipText,
}: TableSectionProps<T>): ReactElement => {
  const getColumnStyle = (col: Column<T>): { width: string } => {
    if (!col.width) {
      return { width: 'auto' };
    }
    if (typeof col.width === 'number') {
      return { width: `${col.width}%` };
    }
    return { width: col.width };
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  const totalColumns = columns.length + (displayActions ? 1 : 0);

  return (
    <div className="overflow-x-auto border border-gray-200 rounded-lg shadow-sm opacity-90">
      <table className="min-w-full text-sm">
        <colgroup>
          {columns.map((col) => (
            <col key={`col-${String(col.key)}`} style={getColumnStyle(col)} />
          ))}
          {displayActions && <col style={{ width: 'auto' }} />}
        </colgroup>
        <thead className="bg-primary-200 text-white">
          <tr>
            {columns.map((col) => (
              <th
                key={String(col.key)}
                className={`px-4 py-4 text-left font-normal ${
                  col.sortable
                    ? 'cursor-pointer select-none hover:bg-primary-300 transition-colors'
                    : ''
                }`}
                onClick={() => col.sortable && onSort(col.key)}
              >
                <div className="flex items-center gap-2">
                  {col.label}
                  {sortConfig?.key === col.key && (
                    <span className="text-xs">
                      {sortConfig.direction === 'asc' ? '▲' : '▼'}
                    </span>
                  )}
                </div>
              </th>
            ))}
            {displayActions ? (
              <th className="px-4 py-3 text-center font-normal">Actions</th>
            ) : null}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 bg-white">
          {data.length > 0 ? (
            data.map((item) => (
              <TableDataRow
                key={item.id}
                item={item}
                columns={columns}
                onView={onView}
                onDelete={onDelete}
                displayActions={displayActions}
                deleteTooltipText={deleteTooltipText}
              />
            ))
          ) : (
            <tr>
              <td
                colSpan={totalColumns}
                className="px-4 py-8 text-center text-gray-500"
              >
                No data available
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

const CustomEntityTable = <T extends WithId>({
  columns,
  data,
  totalData,
  iconName,
  isLoading,
  onView,
  onDelete,
  title,
  onAdd,
  addButtonText = 'Add',
  displayActions = true,
  deleteTooltipText,
  subHeader,
}: CustomEntityTableProps<T>): ReturnType<FC<CustomEntityTableProps<T>>> => {
  const [sortConfig, setSortConfig] = useState<{
    key: keyof T;
    direction: 'asc' | 'desc';
  } | null>(null);

  const sortedData = useMemo(() => {
    if (!sortConfig) {
      return data;
    }

    return [...data].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [data, sortConfig]);

  const handleSort = (key: keyof T): void => {
    if (!sortConfig || sortConfig.key !== key) {
      setSortConfig({ key, direction: 'asc' });
      return;
    }

    if (sortConfig.direction === 'asc') {
      setSortConfig({ key, direction: 'desc' });
      return;
    }

    setSortConfig(null);
  };

  return (
    <div className="mt-5 mx-4 md:mx-8">
      <HeaderSection
        title={title}
        totalData={totalData}
        onAdd={onAdd}
        addButtonText={addButtonText}
        iconName={iconName}
      />
      {subHeader}
      <TableSection
        columns={columns}
        data={sortedData}
        isLoading={isLoading}
        sortConfig={sortConfig}
        onSort={handleSort}
        onView={onView}
        onDelete={onDelete}
        displayActions={displayActions}
        deleteTooltipText={deleteTooltipText}
      />
    </div>
  );
};

export default CustomEntityTable;
